import { useState } from 'react';
import { Highlighter, MessageSquare, BookMarked, SquareStack, BookText, ListPlus } from 'lucide-react';
import { cn } from '../../lib/cn';

const HIGHLIGHT_COLORS = [
  { key: 'neutral', swatch: 'bg-stone-300 dark:bg-stone-500', label: 'Neutral' },
  { key: 'amber',   swatch: 'bg-amber-300 dark:bg-amber-500', label: 'Attention' },
  { key: 'rose',    swatch: 'bg-rose-300 dark:bg-rose-500',   label: 'Concern' },
  { key: 'sky',     swatch: 'bg-sky-300 dark:bg-sky-500',     label: 'Connection' },
];

/**
 * Floating selection toolbar. Rendered at a viewport coordinate derived from
 * the current selection's bounding rect.
 *
 * Props:
 *   rect:        { top, left, width, height } of the selection
 *   selection:   { text, html, text_start, text_end, wordCount, expandedWord }
 *   onHighlight:    (color) => void
 *   onComment:      (note) => void
 *   onCloseReading: ({ title, note }) => void
 *   onAnalyze:      () => void
 *   onLookUp:       () => void — invoked only when a single-word selection
 *   onDismiss:      () => void
 */
export default function SelectionToolbar({
  rect,
  selection,
  onHighlight,
  onComment,
  onCloseReading,
  onAnalyze,
  onLookUp,
  courseLists = null,
  onAddToList,
  onDismiss,
}) {
  const [mode, setMode] = useState('idle'); // idle | highlight | comment | close_reading | add_to_list
  const [noteInput, setNoteInput] = useState('');
  const [titleInput, setTitleInput] = useState('');

  if (!rect || !selection) return null;

  const canLookUp = selection.wordCount === 1;
  const lists = Array.isArray(courseLists) ? courseLists : [];
  const canAddToList = canLookUp && lists.length > 0 && typeof onAddToList === 'function';

  const handlePickList = (listId) => {
    onAddToList(listId);
    onDismiss();
  };
  const lookupLabel = canLookUp && selection.expandedWord
    ? `Look up "${selection.expandedWord}"`
    : 'Look up word';

  const x = rect.left + rect.width / 2;
  const y = rect.top - 8; // 8px margin above selection

  return (
    <div
      data-selection-toolbar
      className="fixed z-50 pointer-events-auto"
      style={{ left: `${x}px`, top: `${y}px`, transform: 'translate(-50%, -100%)' }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl">
        {mode === 'idle' && (
          <div className="flex items-stretch divide-x divide-gray-200 dark:divide-gray-700">
            <ToolbarButton
              icon={Highlighter}
              label="Highlight"
              onClick={() => setMode('highlight')}
            />
            <ToolbarButton
              icon={MessageSquare}
              label="Comment"
              onClick={() => setMode('comment')}
            />
            <ToolbarButton
              icon={BookMarked}
              label="Close reading"
              onClick={() => setMode('close_reading')}
            />
            <ToolbarButton
              icon={SquareStack}
              label="Analyze sentence"
              onClick={onAnalyze}
            />
            <ToolbarButton
              icon={BookText}
              label={lookupLabel}
              onClick={canLookUp ? onLookUp : undefined}
              disabled={!canLookUp}
            />
            {canAddToList && (
              <ToolbarButton
                icon={ListPlus}
                label="Add to list"
                onClick={() => (lists.length === 1 ? handlePickList(lists[0].id) : setMode('add_to_list'))}
              />
            )}
          </div>
        )}

        {mode === 'add_to_list' && (
          <div className="p-2 w-64 max-h-56 overflow-y-auto">
            <div className="px-1 pb-1 text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
              Add &ldquo;{selection.expandedWord}&rdquo; to
            </div>
            <div className="space-y-0.5">
              {lists.map((list) => (
                <button
                  key={list.id}
                  onClick={() => handlePickList(list.id)}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <span className="block text-xs text-gray-700 dark:text-gray-300">{list.name}</span>
                  {list.course_title && (
                    <span className="block text-[11px] text-gray-400 dark:text-gray-500">{list.course_title}</span>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={() => setMode('idle')}
              className="mt-1 px-2 text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
        )}

        {mode === 'highlight' && (
          <div className="p-2 flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 pr-1">Color:</span>
            {HIGHLIGHT_COLORS.map(({ key, swatch, label }) => (
              <button
                key={key}
                onClick={() => { onHighlight(key); onDismiss(); }}
                className={cn(
                  'w-6 h-6 rounded-full border border-gray-300 dark:border-gray-600',
                  swatch,
                  'hover:scale-110 transition-transform'
                )}
                title={label}
              />
            ))}
            <button
              onClick={() => setMode('idle')}
              className="ml-1 text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
        )}

        {mode === 'comment' && (
          <div className="p-3 w-72">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Comment
            </label>
            <textarea
              autoFocus
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              rows={3}
              className="w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Your note…"
            />
            <div className="mt-2 flex justify-end gap-2">
              <button
                onClick={() => setMode('idle')}
                className="text-xs px-2 py-1 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => { if (noteInput.trim()) { onComment(noteInput.trim()); onDismiss(); } }}
                disabled={!noteInput.trim()}
                className="text-xs px-3 py-1 rounded bg-gray-900 text-white dark:bg-white dark:text-gray-900 disabled:opacity-40"
              >
                Save
              </button>
            </div>
          </div>
        )}

        {mode === 'close_reading' && (
          <div className="p-3 w-80">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Title
            </label>
            <input
              autoFocus
              value={titleInput}
              onChange={(e) => setTitleInput(e.target.value)}
              className="w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="e.g. Opening motif"
            />
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mt-2 mb-1">
              Note
            </label>
            <textarea
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              rows={3}
              className="w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="What's happening in this passage…"
            />
            <div className="mt-2 flex justify-end gap-2">
              <button
                onClick={() => setMode('idle')}
                className="text-xs px-2 py-1 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (titleInput.trim() && noteInput.trim()) {
                    onCloseReading({ title: titleInput.trim(), note: noteInput.trim() });
                    onDismiss();
                  }
                }}
                disabled={!titleInput.trim() || !noteInput.trim()}
                className="text-xs px-3 py-1 rounded bg-gray-900 text-white dark:bg-white dark:text-gray-900 disabled:opacity-40"
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>

      <div
        className="w-0 h-0 mx-auto border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white dark:border-t-gray-900"
        style={{ filter: 'drop-shadow(0 1px 0 #e5e7eb)' }}
      />
    </div>
  );
}

function ToolbarButton({ icon: Icon, label, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={cn(
        'flex items-center gap-1.5 px-3 py-2 text-xs font-medium',
        'text-gray-700 dark:text-gray-200',
        disabled
          ? 'opacity-40 cursor-not-allowed'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800'
      )}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
